import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import {
  useGetSubscribersQuery,
  useSetSubscriberActiveMutation,
  useRemoveSubscriberMutation,
} from '../../api/subscriberApi';
import { useGetManagerGroupsQuery } from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import StatusDots from '../../components/StatusDots/StatusDots';
import Badge from '../../components/Badge/Badge';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';

export default function SubscribersPage() {
  const navigate = useNavigate();
  const showToast = useToast();
  const [groupId, setGroupId] = useState('all');

  const { data: groups } = useGetManagerGroupsQuery();
  const groupOptions = useMemo(
    () => [{ label: 'All groups', value: 'all' }, ...(groups ?? []).map((g) => ({ label: g.name, value: g.id }))],
    [groups],
  );

  const {
    data: subscribers,
    isLoading,
    isError,
    error,
  } = useGetSubscribersQuery(groupId === 'all' ? undefined : groupId);
  const [setActive] = useSetSubscriberActiveMutation();
  const [removeSubscriber] = useRemoveSubscriberMutation();

  useEffect(() => {
    if (isError) showToast('error', 'Subscribers', errMsg(error, 'Could not load subscribers.'));
  }, [isError, error, showToast]);

  const tooltipRef = useRef(null);
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [subscribers]);

  async function handleToggleActive(subscriber) {
    try {
      await setActive({ id: subscriber.id, isActive: !subscriber.is_active }).unwrap();
      showToast('success', 'Subscribers', subscriber.is_active ? 'Subscriber disabled.' : 'Subscriber enabled.');
    } catch (err) {
      showToast('error', 'Subscribers', errMsg(err, 'Could not update subscriber.'));
    }
  }

  async function handleRemove(subscriber) {
    try {
      await removeSubscriber(subscriber.id).unwrap();
      showToast('success', 'Subscribers', 'Subscriber removed.');
    } catch (err) {
      showToast('error', 'Subscribers', errMsg(err, 'Could not remove subscriber.'));
    }
  }

  const goEdit = (subscriber) => navigate(`${subscriber.id}/edit`);

  const actionBodyTemplate = (subscriber) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => goEdit(subscriber)} />
      <Button
        icon={`pi ${subscriber.is_active ? 'pi-ban' : 'pi-check'}`}
        text
        className={subscriber.is_active ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleActive(subscriber)}
      />
      <Button
        icon="pi pi-trash"
        text
        className={table.iconDanger}
        onClick={() => handleRemove(subscriber)}
      />
    </div>
  );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Subscribers</h1>
          <p className={`${s.subtitle} textMuted`}>People subscribed to your alerts.</p>
        </div>
        <Button
          label="Add subscriber"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={() => navigate('create')}
        />
      </div>

      <div className={`field ${s.orgSelect}`}>
        <label htmlFor="group">Group</label>
        <Dropdown
          inputId="group"
          value={groupId}
          onChange={(e) => setGroupId(e.value)}
          options={groupOptions}
          className="w-full"
        />
      </div>

      <div className={`${table.tableWrapper} ${table.reverseCard}`}>
        <Tooltip ref={tooltipRef} target={`.${table.statusDot}`} position="left" />
        <DataTable
          value={subscribers ?? []}
          loading={isLoading}
          emptyMessage="No subscribers yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="email"
            header="Email"
            className={table.contactCol}
            body={(sub) => <div className={`${table.cellInsider} ${table.cellWrap}`}>{sub.email}</div>}
          />
          <Column
            header="Phone"
            className={table.contactCol}
            body={(sub) => (
              <div className={`${table.cellInsider} ${s.mutedCell}`}>{sub.phone || '—'}</div>
            )}
          />
          <Column
            header="Status"
            className={table.statusCol}
            body={(sub) => (
              <div className={table.cellInsider}>
                <div className={table.statusBadgesDesktop}>
                  <Badge
                    background={sub.email_verified ? 'var(--color-badge-info-bg)' : 'var(--color-badge-neutral-bg)'}
                    color={sub.email_verified ? 'var(--color-success)' : 'var(--color-text-muted)'}
                  >
                    {sub.email_verified ? 'verified' : 'unverified'}
                  </Badge>
                  {sub.is_external && (
                    <Badge background="var(--color-badge-warn-bg)" color="var(--color-badge-warn-text)">
                      external
                    </Badge>
                  )}
                  {!sub.is_active && (
                    <Badge background="var(--color-badge-error-bg)" color="var(--color-error)">
                      disabled
                    </Badge>
                  )}
                </div>
                <StatusDots
                  items={[
                    {
                      label: sub.email_verified ? 'verified' : 'unverified',
                      color: sub.email_verified ? 'var(--color-success)' : 'var(--color-text-muted)',
                    },
                    ...(sub.is_external ? [{ label: 'external', color: 'var(--color-badge-accent)' }] : []),
                    ...(!sub.is_active ? [{ label: 'disabled', color: 'var(--color-error)' }] : []),
                  ]}
                />
              </div>
            )}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '250px' }}
          />
        </DataTable>
      </div>
    </div>
  );
}
